import { DocumentClassModel } from "@/Common/Models/DocumentClassModel";
import { CheckboxTarget } from "@/Common/Models/UI/Inputs/CheckboxTarget";
import { CheckboxUIModel } from "@/Common/Models/UI/Inputs/CheckboxUIModel";
import {
    DocumentClass,
    DocumentSubClassClinicalNote,
    DocumentSubClassConsultationReports,
    DocumentSubClassForms,
    DocumentSubClassMedications,
    DocumentSubClassOther,
    DocumentSubClassRequests,
    DocumentSubClassResults,
    DocumentSubClassScannedPaperRecord,
    DocumentSubClassSummary
} from "@/Common/Resources/Lookups/Enums/DocumentClasses";

export class DocumentTreeClassControl {
    public static GetDocumentClasses(existing?: Immutable.Map<string, DocumentClassModel>): Immutable.Map<string, DocumentClassModel> {
        const docClasses: DocumentClassModel[] = [
            this.getDocClassFromLookup(DocumentClass.Summary, DocumentSubClassSummary.Code),
            this.getDocClassFromLookup(DocumentClass.ClinicalNote, DocumentSubClassClinicalNote.Code),
            this.getDocClassFromLookup(DocumentClass.ConsultationReports, DocumentSubClassConsultationReports.Code),
            this.getDocClassFromLookup(DocumentClass.Results, DocumentSubClassResults.Code),
            this.getDocumentSubClassForms(),
            this.getDocClassFromLookup(DocumentClass.MedicationAndPrescriptions, DocumentSubClassMedications.Code),
            this.getDocClassFromLookup(DocumentClass.Requests, DocumentSubClassRequests.Code),
            this.getDocClassFromLookup(DocumentClass.Other, DocumentSubClassOther.Code),
            this.getDocClassFromLookup(DocumentClass.ScannedPaperRecord, DocumentSubClassScannedPaperRecord.Code)
        ].filter(p => !!p);

        if (existing)
            docClasses.mutate(p => p.Update(existing.get(p.LookupCode)));

        return Immutable.Map<string, DocumentClassModel>(docClasses.map(item => [item.LookupCode, item]));
    }

    public static GetDocumentClassesAsCheckboxUIModel(target: CheckboxTarget): CheckboxUIModel[] {
        return DocumentTreeClassControl.GetDocumentClasses().map(p => new CheckboxUIModel({
            Id: p.LookupId,
            Name: p.Name,
            Target: target
        })).toArray();
    }

    public static getDocClassFromLookup(docClassLookupCode: string, docSubClassType?: string): DocumentClassModel | null {
        const docClassLookup = cacheStore.FindLookupByLookupCode(docClassLookupCode);

        if (!docClassLookup)
            return null;

        const docClass = DocumentClassModel.FromLookup(docClassLookup);

        if (docSubClassType) {
            const subclassesLookups = cacheStore
                .FindLookupsByPartialType(docSubClassType)
                .sort((a, b) => a.LookupPosition > b.LookupPosition ? 1 : -1);

            docClass.SubClasses = Immutable.Map<string, DocumentClassModel>(
                subclassesLookups
                    .map(item => {
                        const doc = DocumentClassModel.FromLookup(item);
                        return [doc.LookupCode, doc];
                    })
            );
        }
        return docClass;
    }

    public static getDefaultSubclassForIndexingFax(docClassLookupCode: string): string {
        switch (docClassLookupCode) {
            case DocumentClass.Summary:
                return this.getDocClassFromLookup(
                    DocumentClass.Summary,
                    DocumentSubClassSummary.Code
                ).SubClasses.get(DocumentSubClassSummary.MedicalSummary).LookupId;
            case DocumentClass.ClinicalNote:
                return this.getDocClassFromLookup(
                    DocumentClass.ClinicalNote,
                    DocumentSubClassClinicalNote.Code
                ).SubClasses.get(DocumentSubClassClinicalNote.FamilyDoctor).LookupId;
            case DocumentClass.ConsultationReports:
                return this.getDocClassFromLookup(
                    DocumentClass.ConsultationReports,
                    DocumentSubClassConsultationReports.Code
                ).SubClasses.get(DocumentSubClassConsultationReports.Specialist).LookupId;
            case DocumentClass.Results:
                return this.getDocClassFromLookup(
                    DocumentClass.Results,
                    DocumentSubClassResults.Code
                ).SubClasses.get(DocumentSubClassResults.Laboratory).LookupId;
            case DocumentClass.Forms:
                return this.getDocClassFromLookup(
                    DocumentClass.Forms,
                    DocumentSubClassForms.Code
                ).SubClasses.get(DocumentSubClassForms.Other).LookupId;
            case DocumentClass.MedicationAndPrescriptions:
                return this.getDocClassFromLookup(
                    DocumentClass.MedicationAndPrescriptions,
                    DocumentSubClassMedications.Code
                ).SubClasses.get(DocumentSubClassMedications.Prescription).LookupId;
            case DocumentClass.Requests:
                return this.getDocClassFromLookup(
                    DocumentClass.Requests,
                    DocumentSubClassRequests.Code
                ).SubClasses.get(DocumentSubClassRequests.LaboratoryRequests).LookupId;
            case DocumentClass.Other:
                return this.getDocClassFromLookup(
                    DocumentClass.Other,
                    DocumentSubClassOther.Code
                ).SubClasses.get(DocumentSubClassOther.Other).LookupId;
            case DocumentClass.ScannedPaperRecord:
                return this.getDocClassFromLookup(
                    DocumentClass.ScannedPaperRecord,
                    DocumentSubClassScannedPaperRecord.Code
                ).SubClasses.get(DocumentSubClassScannedPaperRecord.ClinicalNotes).LookupId;
        }
    }

    private static getDocumentSubClassForms(): DocumentClassModel {
        let doc = this.getDocClassFromLookup(DocumentClass.Forms, DocumentSubClassForms.Code);

        if (!sessionStore.getCurrentEmployee().isActiveSiteQuebec()) {
            doc.SubClasses = doc.SubClasses
                .remove(DocumentSubClassForms.CSST)
                .remove(DocumentSubClassForms.RAMQ)
                .remove(DocumentSubClassForms.SAAQ);
        }

        return doc;
    }
}
